"use client";

import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { User, type Session, type AuthChangeEvent } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { createClient } from "@/utils/supabase/client";
import * as Sentry from "@sentry/nextjs";
import {
    readProfileSnapshot,
    writeProfileSnapshot,
    clearProfileSnapshots,
    resolveBootState,
    applyVerificationSuccess,
    applyVerificationFailure,
    shouldDeferToBoot,
    type StorageLike,
} from "@/lib/auth-boot";
import { clearDeviceCache, getDeviceCacheOwner, setDeviceCacheOwner } from "@/lib/device-cache";

interface UserProfile {
    id: string;
    userId: string;
    fullName: string | null;
    avatarUrl: string | null;
}

interface AuthContextType {
    user: User | null;
    profile: UserProfile | null;
    loading: boolean;
    /** False while `profile`/`user` are restored-from-cache guesses; true once the server has confirmed them. */
    verified: boolean;
    refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ user: null, profile: null, loading: true, verified: false, refreshProfile: async () => {} });

/** window.localStorage guarded for private-mode / restricted contexts where even the property read can throw. */
const getStorage = (): StorageLike | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [verified, setVerified] = useState(false);
    const supabase = createClient();
    const queryClient = useQueryClient();
    const prevUserIdRef = useRef<string | null>(null);
    // True while bootAuth's own getSession()/getUser() round trip is still in
    // flight. Lets handleAuthEvent recognize an event as the exact initial
    // session bootAuth is already restoring/verifying, instead of redoing the
    // same fetch and state writes a second time.
    const bootInFlightRef = useRef(true);
    // The user id bootAuth is handling, set the moment its getSession() call
    // resolves (or to null if there is no session) -- before any further await.
    const bootUserIdRef = useRef<string | null>(null);

    // Returns the fetched profile (or null) without touching state — lets the boot
    // effect and onAuthStateChange write a localStorage snapshot from the same
    // network call instead of re-fetching or reading back a not-yet-committed state update.
    const fetchProfileData = useCallback(async (): Promise<UserProfile | null> => {
        try {
            const res = await fetch("/api/profile");
            if (res.ok) {
                const data = await res.json();
                return data;
            }
        } catch {
            // Non-critical — profile will be null
        }
        return null;
    }, []);

    const fetchProfile = useCallback(async () => {
        const data = await fetchProfileData();
        setProfile(data);
        // Also refresh the on-device snapshot so the next cold boot restores the
        // just-edited profile instead of flashing the stale cached name until
        // server verification corrects it.
        const uid = prevUserIdRef.current;
        if (data && uid) writeProfileSnapshot(getStorage(), uid, data);
    }, [fetchProfileData]);

    useEffect(() => {
        // WHY (2026-09-08): this boot effect used to await getUser() then, if a user
        // existed, ALSO await fetchProfile() before ever calling setLoading(false) —
        // and `{!loading && children}` below rendered nothing until then. Every app
        // open was a blank screen for two sequential network round trips, worse on a
        // cold serverless function on a phone. Fix: restore the last verified
        // user+profile from localStorage synchronously (no network) so children can
        // render immediately, then always re-verify with the server in the
        // background and correct the screen silently if it disagrees.
        const bootAuth = async () => {
            const storage = getStorage();

            let localUser: User | null = null;
            try {
                const { data: { session } } = await supabase.auth.getSession();
                localUser = session?.user ?? null;
            } catch {
                localUser = null;
            }
            // Record who bootAuth is handling as soon as we know it, so a
            // concurrent onAuthStateChange firing for this same session can
            // recognize it and stand down instead of duplicating the work below.
            bootUserIdRef.current = localUser?.id ?? null;

            try {
                const snapshot = localUser ? readProfileSnapshot<UserProfile>(storage, localUser.id) : null;
                const boot = resolveBootState({ localUser: localUser ? { id: localUser.id } : null, snapshot });

                if (boot.status === "authed-unverified" && localUser) {
                    setUser(localUser);
                    setProfile(snapshot);
                    setVerified(false);
                    setLoading(false);
                    prevUserIdRef.current = localUser.id;
                }
                // else: leave `loading` at its initial `true` — same as before this change
                // (first-ever login on this device has no snapshot to restore from yet).

                // Always verify with the server, regardless of whether we restored anything.
                const { data: { user: verifiedUser } } = await supabase.auth.getUser();

                if (verifiedUser) {
                    // The optimistic restore above (if any) guessed a different identity
                    // than the server just confirmed — drop the stale snapshot so a future
                    // boot on this device never restores the wrong account again.
                    if (localUser && localUser.id !== verifiedUser.id) {
                        clearProfileSnapshots(storage);
                    }

                    const data = await fetchProfileData();
                    setUser(verifiedUser);
                    setProfile(data);
                    if (data) writeProfileSnapshot(storage, verifiedUser.id, data);
                    prevUserIdRef.current = verifiedUser.id;

                    const verifiedState = applyVerificationSuccess({ user: { id: verifiedUser.id }, profile: data });
                    setVerified(verifiedState.status === "authed-verified");

                    if (getDeviceCacheOwner() !== verifiedUser.id) {
                        await clearDeviceCache();
                    }
                    setDeviceCacheOwner(verifiedUser.id);
                } else {
                    clearProfileSnapshots(storage);
                    setUser(null);
                    setProfile(null);
                    prevUserIdRef.current = null;

                    const failedState = applyVerificationFailure();
                    setVerified(failedState.status === "authed-verified"); // always false
                }

                setLoading(false);
            } finally {
                // Covers both branches above (verified vs not) and an unexpected
                // throw — bootAuth's exclusive claim on the initial session never
                // gets stuck "in flight" forever.
                bootInFlightRef.current = false;
            }
        };

        bootAuth();

        // Runs the real auth-event handling. Kept as a separate async function so
        // the onAuthStateChange callback registered below can stay synchronous.
        const handleAuthEvent = async (event: AuthChangeEvent, session: Session | null) => {
            const newUser = session?.user ?? null;
            const newUserId = newUser?.id ?? null;
            const storage = getStorage();

            if (
                shouldDeferToBoot({
                    bootInFlight: bootInFlightRef.current,
                    event,
                    eventUserId: newUserId,
                    bootUserId: bootUserIdRef.current,
                })
            ) {
                // bootAuth's own getSession()/getUser() round trip already owns this
                // exact session and will write user/profile/loading/verified itself —
                // fetching the profile again here would just be a duplicate request.
                // Only keep Sentry's identity current in case something crashes before
                // bootAuth finishes.
                if (newUser) Sentry.setUser({ id: newUser.id, email: newUser.email });
                return;
            }

            // A different person is now signed in on this device: another tab signed
            // in as someone else (supabase-js broadcasts that SIGNED_IN to this tab),
            // or this tab read that person's session back from the shared storage
            // when it became visible again (a SIGNED_IN, or a TOKEN_REFRESHED if the
            // session was due a refresh). Never a sign-out (there is no new user), a
            // token refresh or repeat SIGNED_IN for the same person (same id; every
            // return to the tab sends one), or this tab's first sign-in (no previous
            // user). Read before the clears below await anything.
            const previousUserId = prevUserIdRef.current;
            const switchedAccount = previousUserId !== null && newUserId !== null && newUserId !== previousUserId;

            // Clear all cached query data when user signs out or switches accounts
            if (event === "SIGNED_OUT" || (prevUserIdRef.current && newUserId !== prevUserIdRef.current)) {
                Sentry.setUser(null);
                queryClient.clear();
                setProfile(null);
                setVerified(false);
                // Clear session-specific localStorage to prevent data leaking across accounts
                localStorage.removeItem("activeQuoteId");
                localStorage.removeItem("buildsynx:activeEstimation");
                localStorage.removeItem("taskSortPreferences");
                localStorage.removeItem("drywall-erp:favoriteProjects");
                localStorage.removeItem("finance:billReportHstOnly");
                localStorage.removeItem("finance:billReportPaidFrom");
                sessionStorage.clear();
                clearProfileSnapshots(storage);
                await clearDeviceCache();

                if (switchedAccount && typeof window !== "undefined") {
                    // WHY a full page load, before the new user is set: the clears above
                    // do not reach everything the previous account left in this tab.
                    // `user` never becomes null in a switch, so the dashboard's provider
                    // tree stays mounted and every provider keeps its own memory —
                    // QuotesProvider's list cache is keyed by search and filters with no
                    // user id, so it would paint the previous account's quotes for up to
                    // 2 minutes without a request, and SettingsProvider loads the company
                    // settings once per mount. Next keeps its router cache in memory too,
                    // its back/forward entries included. A page load resets all of it in
                    // one move. Returning before setUser means this tab never renders the
                    // new account on top of the old one's memory; prevUserIdRef keeps the
                    // old id, so any later event for the new account lands here again.
                    window.location.replace("/");
                    return;
                }
            }

            prevUserIdRef.current = newUserId;
            setUser(newUser);

            // Fetch profile for newly signed-in user
            if (event === "SIGNED_IN" && newUser) {
                Sentry.setUser({ id: newUser.id, email: newUser.email });
                const data = await fetchProfileData();
                setProfile(data);
                if (data) writeProfileSnapshot(storage, newUser.id, data);

                const verifiedState = applyVerificationSuccess({ user: { id: newUser.id }, profile: data });
                setVerified(verifiedState.status === "authed-verified");

                if (getDeviceCacheOwner() !== newUser.id) {
                    await clearDeviceCache();
                }
                setDeviceCacheOwner(newUser.id);
            }

            setLoading(false);
        };

        // supabase-js awaits this callback while holding its internal auth lock
        // (GoTrueClient#_acquireLock), so an async callback here would block every
        // concurrent getSession()/getUser() call — bootAuth's included — until it
        // resolves. Stay synchronous and hand off to handleAuthEvent instead.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            void handleAuthEvent(event, session);
        });

        // Refresh session when user returns to the tab after being away.
        // Supabase JWT expires after ~1 hour of idle; without this, the first
        // click after returning silently fails (nav gets stuck, data fetches 401).
        const refreshSession = () => {
            // getSession() triggers a token refresh if the JWT is expired
            supabase.auth.getSession();
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") refreshSession();
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        // window focus catches the "click back into the window without switching tabs" case
        window.addEventListener("focus", refreshSession);
        // Periodic proactive refresh — covers the "stay on same tab, sit idle" case
        // where neither visibilitychange nor focus ever fires. 10 min < 1h JWT TTL.
        const refreshInterval = window.setInterval(refreshSession, 10 * 60 * 1000);

        return () => {
            subscription.unsubscribe();
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", refreshSession);
            window.clearInterval(refreshInterval);
        };
    }, []);

    return (
        <AuthContext.Provider value={{ user, profile, loading, verified, refreshProfile: fetchProfile }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
